import {Component, OnDestroy, OnInit} from '@angular/core';
import {MoneyPipe} from '../pipes/money.pipe';
import {finalize, firstValueFrom, Subject, takeUntil} from 'rxjs';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {PromotionService} from '../../services/promotion.service';
import {ProfileService} from '../../services/profile.service';
import {RegionService} from '../../services/region-service';
import {PlaceService} from '../../services/place.service';
import {StorageService} from '../../services/storage-service';
import {Region} from '../../models/region';
import {Place} from '../../models/place';
import {Profile} from '../../models/profile';
import {Promotion} from '../../models/promotion';
import {CurrencyPipe, NgForOf, NgIf} from '@angular/common';
import {SeoService} from '../../services/seo.service';
import {environment} from '../../environments/environment';

@Component({
  selector: 'app-affiliate',
  imports: [
    MoneyPipe,
    NgIf,
    RouterLink,
    NgForOf
  ],
  providers: [CurrencyPipe],
  templateUrl: './affiliate.component.html',
  styleUrl: './affiliate.component.scss'
})
export class AffiliateComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<boolean>();

  NUM_SPACES = environment.NUM_SPACES

  // Ref
  ref: string | null = null

  // Region
  isRegionLoading = false
  isRegionLoaded = false
  regionSlug: string | null = null
  region: Region | null = null

  // Places
  isPlacesLoading = false;
  isPlacesLoaded = false;
  places: Place[] = [];

  // Profile
  isProfileLoading = false;
  isProfileLoaded = false;
  profile: Profile | null = null

  // Promotion
  isPromotionLoading = false;
  isPromotionLoaded = false;
  promotion: Promotion | null = null;

  loading = false;

  constructor(private route: ActivatedRoute,
              private storageService: StorageService,
              private profileService: ProfileService,
              private promotionService: PromotionService,
              private regionService: RegionService,
              private placeService: PlaceService,
              private seoService: SeoService) {
  }

  ngOnInit() {
    this.route.params.pipe(takeUntil(this.onDestroy$)).subscribe(async (params) => {
      this.ref = params['ref'];

      this.regionSlug = await this.regionService.getRegionSlug();

      let regionPromise = this.refreshRegionPromise()
      let placesPromise = this.refreshPlacesPromise()
      let profilePromise = this.refreshProfilePromise()
      let promotionPromise = this.refreshPromotionPromise()

      this.loading = true
      await Promise.all([regionPromise, placesPromise, profilePromise, promotionPromise]).then(results => {
        this.region = results[0] || null
        this.places = results[1] || []
        this.profile = results[2] || null
        this.promotion = results[3] || null

        // Profile should be awarded when the lead converts
        if (this.ref && this.profile) {
          this.storageService.add(StorageService.REF, this.ref)
        }

        // Promotion code
        if (this.promotion) {
          this.storageService.add(StorageService.PROMOTION_CODE, this.promotion.code)
        }

        this.refreshSeo();

      }).finally(() => this.loading = false)
    });
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  refreshProfilePromise() {
    if (!this.ref) return

    this.isProfileLoading = true;
    return firstValueFrom(this.profileService.getByRef(this.ref).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isProfileLoading = false;
        this.isProfileLoaded = true;
      })
    ));
  }

  refreshSeo() {
    if (!this.profile) return;

    const title = `Croissant | ${this.profile.user.firstName} ${this.profile.user.lastName} invites you to join Croissant`
    const description = `Join Croissant with Adam's referral and get a credit on your membership! Access ${this.NUM_SPACES}+ coworking spaces with this exclusive offer. Start your flexible workspace journey today.`

    this.seoService.setTitle(title)
    this.seoService.setDescription(description)
  }

  refreshPromotionPromise() {
    if (!this.ref) return

    this.isPromotionLoading = true;
    return firstValueFrom(this.promotionService.getByRef(this.ref).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isPromotionLoading = false;
        this.isPromotionLoaded = true;
      })
    ));
  }

  refreshRegionPromise() {
    if (!this.regionSlug) return;

    this.isRegionLoading = true;
    return firstValueFrom(this.regionService.getBySlug(this.regionSlug).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isRegionLoading = false;
        this.isRegionLoaded = true;
      })
    ));
  }

  refreshPlacesPromise() {
    if (!this.regionSlug) return;

    let placesByRegionPromise = firstValueFrom(this.placeService.findByRegion(this.regionSlug, false, false, false, 6).pipe(takeUntil(this.onDestroy$)));
    let placesPromise = firstValueFrom(this.placeService.findPlaces(false, false, 6).pipe(takeUntil(this.onDestroy$)));

    this.isPlacesLoading = true
    return Promise.all([placesByRegionPromise, placesPromise]).then(results => {
      let placesByRegions = results[0]
      let places = results[1]

      return Promise.resolve(placesByRegions.length >= 6 ? placesByRegions : places)
    }).finally(() => {
      this.isPlacesLoading = false
      this.isPlacesLoaded = true
    })
  }

}
